import { FC } from 'react';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';
import { QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import moment from 'moment';
import { ThemeProvider } from 'styled-components';

// import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { queryClient } from '@/utils/QueryClient/QueryClient';
import { configureYupLocale } from '@/utils/yupLocale';

import { CompleteReservationProvider } from '@/context/CompleteReservationContext/CompleteReservationContext';
import { ConciliationProvider } from '@/context/ConciliationPage/ConciliationContext';
import { ChecklistProvider } from '@/context/ControllerPage/ChecklistContext';
import { CustomPopupsProvider } from '@/context/CustomPopupsContext/CustomPopupsContext';
import EstimateCartProvider from '@/context/EstimateCart';
import { FileProvider } from '@/context/FileContext/FileContext';
import FilterBarProvider from '@/context/FilterBar';
import { GeolocationProvider } from '@/context/GeolocationContext/GeolocationContext';
import { IdpAuthProvider } from '@/context/IdpAuth/IdpAuthContext';
import { GlobalLoader, LoaderProvider } from '@/context/LoaderContext';
import { MenuLateralProvider } from '@/context/MenuLateral';
import { ModalProvider } from '@/context/ModalContext';
import { AbortRequestProvider } from '@/context/RequestContext/AbortRequestContext';
import { RequestSupportModalProvider } from '@/context/RequestSuportModalContext';
import { SendingVoucherProvider } from '@/context/SendingVoucherContext';
import { ToastProvider } from '@/context/ToastContext';
import { UserProvider } from '@/context/UserContext';
import { ViewModeProvider } from '@/context/ViewMode/ViewMode';

import CustomPopupsLayout from '@/layouts/CustomPopupsLayout/CustomPopupsLayout';

import { ToastInitializer } from '@/components/ToastInitializer/ToastInitializer';
import { ToastManager, ToastV2Provider } from '@/components/ToastV2';

import { FeatureFlagProvider } from './context/FeatureFlagContext/FeatureFlagContext';
import { PostHogProvider } from './context/PosthogContext/PosthogContext';
// import ReCaptchaMsg from '@/components/ReCaptchaMsg';
import Router from './Router';

import GlobalStyles from '@/styles/GlobalStyle';
import theme from '@/styles/themes';

import { localStorageSeguro } from '@/lib/storage';
import store from '@/store';

configureYupLocale();

const tagManagerArgs = {
  gtmId: import.meta.env.REACT_APP_TAG_MANAGER_KEY as string,
};

TagManager.initialize(tagManagerArgs);

ReactGA.initialize([
  {
    trackingId: import.meta.env.REACT_APP_GA_KEY as string,
    gaOptions: {
      siteSpeedSampleRate: 100,
    },
  },
]); // Initialize Google Analytics

const start = moment();
// Sem storage (iframe com cookies de terceiros bloqueados) o app monta igual.
localStorageSeguro()?.setItem('startUse', start.toString());

const App: FC<{}> = () => {
  return (
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <ViewModeProvider>
            <CompleteReservationProvider>
              <SendingVoucherProvider>
                <IdpAuthProvider>
                  <UserProvider>
                    <PostHogProvider>
                      <FeatureFlagProvider>
                        <ChecklistProvider>
                          <EstimateCartProvider>
                            <MenuLateralProvider>
                              <ModalProvider>
                                <ToastV2Provider>
                                  <ToastProvider>
                                    <>
                                      <ToastInitializer />
                                      <FilterBarProvider>
                                        <LoaderProvider>
                                          <FileProvider>
                                            <AbortRequestProvider>
                                              <ConciliationProvider>
                                                <RequestSupportModalProvider>
                                                  <GeolocationProvider>
                                                    <BrowserRouter>
                                                      {/* <ReCaptchaMsg /> */}
                                                      <GlobalStyles />
                                                      <CustomPopupsProvider>
                                                        <CustomPopupsLayout />
                                                      </CustomPopupsProvider>
                                                      <ToastManager />
                                                      <GlobalLoader />
                                                      <Router />
                                                    </BrowserRouter>
                                                  </GeolocationProvider>
                                                </RequestSupportModalProvider>
                                              </ConciliationProvider>
                                            </AbortRequestProvider>
                                          </FileProvider>
                                        </LoaderProvider>
                                      </FilterBarProvider>
                                    </>
                                  </ToastProvider>
                                </ToastV2Provider>
                              </ModalProvider>
                            </MenuLateralProvider>
                          </EstimateCartProvider>
                        </ChecklistProvider>
                      </FeatureFlagProvider>
                    </PostHogProvider>
                  </UserProvider>
                </IdpAuthProvider>
              </SendingVoucherProvider>
            </CompleteReservationProvider>
          </ViewModeProvider>
        </QueryClientProvider>
      </Provider>
    </ThemeProvider>
  );
};

// export const AppWithReCaptcha = () => (
//   <GoogleReCaptchaProvider
//     reCaptchaKey={import.meta.env.REACT_APP_RECAPTCHA_KEY as string}
//     language="pt-BR"
//     container={{
//       element: 'recaptcha-msg-container',
//       parameters: {
//         badge: 'bottomright', // optional, default undefined
//         theme: 'dark', // optional, default undefined
//       },
//     }}
//   >
//     <App />
//   </GoogleReCaptchaProvider>
// );

// export default AppWithReCaptcha;

export default App;
